"use client";

import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import ConfirmedEmailCard from "@/app/components/atoms/ConfirmedEmailCard";
import InfoBanner from "@/app/components/atoms/InfoBanner";
import DynamicForm from "@/app/components/organisms/DynamicForm";
import type { FormConfig } from "@/app/components/types";

const AMBIGUOUS_ACCOUNT_ERROR =
  "We were unable to uniquely identify your account. Please contact tickets@groundsforsculpture.org to update your information.";

const phoneConfig: FormConfig = {
  id: "register-phone-form",
  fields: [
    {
      name: "phone",
      label: "Phone",
      type: "tel",
      placeholder: "(555) 555-5555",
      required: true,
      autoFocus: true,
    },
  ],
  submitLabel: "Continue",
  ariaLabelledBy: "register-phone-header",
};

interface RegisterPhoneStepProps {
  /** The email confirmed in the previous step. */
  email: string;
  /** Edit email using the same interaction as other registration steps. */
  onEditEmail: () => void;
  /** Called with the phone number when the user submits. */
  onContinue: (phone: string) => void;
  /** Whether account creation is in progress. */
  loading?: boolean;
  /** Error message from account creation. */
  error?: string | null;
}

/**
 * @component RegisterPhoneStep
 * @description Phone number entry step for the registration flow. Displays
 * the confirmed email with an edit button, then prompts for a phone number.
 * @param email - The email confirmed in the previous step.
 * @param onEditEmail - Returns the user to the email entry step.
 * @param onContinue - Callback receiving the phone number on submit.
 * @param loading - Shows loading state on the submit button.
 * @param error - Form-level error message.
 * @returns The phone number entry form.
 */
export default function RegisterPhoneStep({
  email,
  onEditEmail,
  onContinue,
  loading,
  error,
}: RegisterPhoneStepProps) {
  const showAmbiguousAccountInfo = error === AMBIGUOUS_ACCOUNT_ERROR;

  const handleSubmit = (data: Record<string, string | boolean>) => {
    onContinue(String(data["phone"] ?? "").trim());
  };

  return (
    <div className="flex flex-col gap-8">
      <StepHeading id="register-phone-header" title="Enter your phone number to continue" />

      <div className="w-full max-w-md m-auto flex flex-col gap-6">
        <ConfirmedEmailCard email={email} onEdit={onEditEmail} />
        <FormCard>
          <DynamicForm
            config={phoneConfig}
            onSubmit={handleSubmit}
            loading={loading}
          />
          {showAmbiguousAccountInfo && (
            <InfoBanner className="mt-2">{AMBIGUOUS_ACCOUNT_ERROR}</InfoBanner>
          )}
          {error && !showAmbiguousAccountInfo && (
            <p role="alert" className="text-body-base text-destructive mt-2 text-center">
              {error}
            </p>
          )}
          <p className="text-center text-base text-charcoal">
            Already have an account?{" "}
            <a
              href="/login"
              className="underline hover:text-accent-green transition-colors"
            >
              Login here
            </a>
          </p>
        </FormCard>
      </div>
    </div>
  );
}
